import React, { useState } from 'react';
import { Form } from 'antd';
import PropsType from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '@/utils/locale';
import { getCurrentCountry } from '@/utils/domain';
import { getCountryCode } from '@/utils/countryCode';
import InputComponent from '@/components/Input';
import ButtonComponent from '@/components/Button';
import InputPhone from '@/components/InputPhone';
import { validateEmail, validatePhoneNumber } from '@/utils/validate';
import SelectInput from '@/components/Select';
import CheckboxInput from '@/components/Checkbox';
import { ENV, ROUTES } from '@/constants';


import './index.scss';

const channels = [
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Google search', value: 'Google search' },
  { label: 'Instagram', value: 'Instagram' },
  { label: 'Youtube', value: 'Youtube' },
  { label: 'Twitter', value: 'Twitter' },
  { label: 'LinkedIn', value: 'LinkedIn' },
  { label: 'Sales Team', value: 'Sales Team' },
  { label: 'Friends', value: 'Friends' },
  { label: 'Others', value: 'Others' },
];

function SignUpContainer({ onSubmit, isLoading }) {
  const { t: translation } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [formInstance] = Form.useForm();
  const lang = getCurrentLanguage();

  // get the current Country
  const country = getCurrentCountry();

  // get the current Country code
  const countryCode = getCountryCode(country);

  const navigate = useNavigate();
  const listItem = [
    translation('login.banner.1'),
    translation('login.banner.2'),
    translation('login.banner.3'),
    translation('login.banner.4'),
    translation('login.banner.5'),
    translation('login.banner.6'),
  ];

  const renderItem = (key, item) => (
    <div key={key} className="signup-page__item">
      <span className="check-icon" />
      <p className="mb-0">{item}</p>
    </div>
  );

  const handleSignUp = (e) => {
    onSubmit({
      ...e,
      phoneNumber: `${countryCode}${e.phoneNumber}`,
    });
  };

  const handleCheckTerm = (e) => {
    setChecked(e.target.checked);
  };

  const openLink = (path) => {
    navigate(path);
  };

  const handleOnChangePassword = () => {
    formInstance.resetFields(['confirmPassword']);
  };

  const websiteURLBaseOnLanguage =
    lang === 'th' ? ENV.WEBSITE_URL : `${ENV.WEBSITE_URL}/${lang}`;

  return (
    <div className="signup-page">
      <div className="signup-page__container">
        <div className="signup-page__left">
          <div className="logo-wrapper">
            <a href={websiteURLBaseOnLanguage}>
              <div className="logo" />
            </a>
          </div>
          <div className="signup-page__title">{translation('login.title')}</div>
          <div className="signup-page__left__group">
            {listItem.map((item, key) => renderItem(key, item))}
          </div>
        </div>
        <div className="signup-page__right">
          <div className="signup-page__right__form">
            <h1 className="signup-page__right__title ">
              {translation('sign_up.form.title')}
            </h1>
            <span className="signup-page__right__desc">
              {translation('sign_up.form.have_account')}
              <span
                className="signup-page__right__desc --link"
                aria-hidden="true"
                onClick={() => openLink(ROUTES.LOGIN_PATH)}
              >
                {translation('sign_up.login_link')}
              </span>
            </span>
            <Form
              className="signup-page__right__form__container"
              name="signup-form"
              layout="vertical"
              onFinish={handleSignUp}
              autoComplete="off"
              form={formInstance}
            >
              <Form.Item
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: translation(
                      'sign_up.form.errors.first_name.required',
                    ),
                  },
                ]}
              >
                <InputComponent
                  placeholder={translation(
                    'sign_up.form.placeholder.first_name',
                  )}
                  label={translation('sign_up.form.fields.first_name')}
                />
              </Form.Item>
              <Form.Item
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: translation(
                      'sign_up.form.errors.last_name.required',
                    ),
                  },
                ]}
              >
                <InputComponent
                  placeholder={translation(
                    'sign_up.form.placeholder.last_name',
                  )}
                  label={translation('sign_up.form.fields.last_name')}
                />
              </Form.Item>
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: translation('login.form.errors.email.required'),
                  },
                  () => ({
                    validator(_, value) {
                      if (value && !validateEmail(value)) {
                        return Promise.reject(
                          new Error(
                            translation('login.form.errors.email.not_valid'),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputComponent
                  placeholder={translation('login.form.placeholder.email')}
                  label={translation('login.form.fields.email')}
                />
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                rules={[
                  {
                    required: true,
                    message: translation(
                      'sign_up.form.errors.phone_number.required',
                    ),
                  },
                  () => ({
                    validator(_, value) {
                      if (value && !validatePhoneNumber(countryCode, value)) {
                        return Promise.reject(
                          new Error(
                            translation(
                              'sign_up.form.errors.phone_number.invalid',
                            ),
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputPhone
                  placeholder={translation(
                    'sign_up.form.placeholder.phone_number',
                  )}
                  label={translation('sign_up.form.fields.phone_number')}
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: translation('login.form.errors.password.required'),
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (
                        !value ||
                        !getFieldValue('confirmPassword') ||
                        getFieldValue('confirmPassword') === value
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          translation(
                            'sign_up.form.errors.confirm_password.not_match',
                          ),
                        ),
                      );
                    },
                  }),
                ]}
              >
                <InputComponent
                  type="password"
                  placeholder={translation('login.form.placeholder.password')}
                  label={translation('login.form.fields.password')}
                  onChange={handleOnChangePassword}
                />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                rules={[
                  {
                    required: true,
                    message: translation(
                      'sign_up.form.errors.confirm_password.required',
                    ),
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (
                        !value ||
                        getFieldValue('password') === '' ||
                        getFieldValue('password') === value
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          translation(
                            'sign_up.form.errors.confirm_password.not_match',
                          ),
                        ),
                      );
                    },
                  }),
                ]}
              >
                <InputComponent
                  placeholder={translation(
                    'sign_up.form.placeholder.confirm_password',
                  )}
                  type="password"
                />
              </Form.Item>
              <Form.Item
                name="channel"
                rules={[
                  {
                    required: true,
                    message: translation(
                      'sign_up.form.errors.channel.required',
                    ),
                  },
                ]}
              >
                <SelectInput
                  label={translation('sign_up.form.fields.channel')}
                  placeholder={translation('sign_up.form.placeholder.channel')}
                  options={channels}
                />
              </Form.Item>
              <CheckboxInput
                className="signup-page__term"
                checked={checked}
                onChange={handleCheckTerm}
              >
                <span className="signup-page__term__text">
                  {translation('sign_up.form.read_agree')}
                  <a
                    className="signup-page__term__text --link"
                    href={process.env.REACT_APP_TERM_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {translation('sign_up.form.terms')}
                  </a>
                  {translation('sign_up.form.and')}
                  <a
                    className="signup-page__term__text --link"
                    href={process.env.REACT_APP_PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {translation('sign_up.form.privacy')}
                  </a>{' '}
                  {lang === 'en' ? 'already' : ''}
                </span>
              </CheckboxInput>
              <Form.Item className="signup-page__submit-btn">
                <ButtonComponent
                  loading={isLoading}
                  type="primary"
                  htmlType="submit"
                  disabled={!checked}
                >
                  {translation('login.register_text')}
                </ButtonComponent>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}

SignUpContainer.propTypes = {
  onSubmit: PropsType.func,
  isLoading: PropsType.bool,
};

export default SignUpContainer;
